/**
 * EmptyState (HD-PASS 3, 2026-09-02) — ONE empty / error / nothing-here
 * language for every surface. Before this each page rolled its own: a grey
 * icon over 12px italic text on the Board, "Nothing here — inbox zero." at
 * 11px in the Inbox, a bare "No rows" in report tables.
 *
 *   <EmptyState icon={Calendar} title="No dispatches on this date"
 *     hint="Either nothing's scheduled, or this is a weekend or holiday." />
 *
 * `tone` colors the icon chip (slate = neutral, emerald = all clear / done,
 * amber = needs attention, red = error). `size="sm"` is for inside a column
 * or a card; `md` for a page or pane.
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

export function EmptyState({
  icon: Icon,
  title,
  hint,
  action,
  tone = "slate",
  size = "md",
  className,
}: {
  icon?: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: ReactNode;
  hint?: ReactNode;
  /** A button or link rendered under the hint. */
  action?: ReactNode;
  tone?: "slate" | "emerald" | "amber" | "red" | "brand";
  size?: "sm" | "md";
  className?: string;
}) {
  const chip = {
    slate: "bg-white text-slate-400 shadow-e1 ring-1 ring-black/[0.05]",
    emerald: "bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100",
    amber: "bg-amber-50 text-amber-600 ring-1 ring-inset ring-amber-100",
    red: "bg-red-50 text-red-600 ring-1 ring-inset ring-red-100",
    brand: "bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100",
  }[tone];
  const sm = size === "sm";
  return (
    <div className={cn("flex flex-col items-center text-center", sm ? "px-4 py-8" : "px-8 py-14", className)}>
      {Icon && (
        <span className={cn("flex items-center justify-center", sm ? "h-11 w-11 rounded-2xl" : "h-16 w-16 rounded-3xl", chip)}>
          <Icon className={sm ? "h-5 w-5" : "h-7 w-7"} strokeWidth={sm ? 2 : 1.6} />
        </span>
      )}
      <p className={cn("font-bold tracking-tight text-slate-900", Icon && (sm ? "mt-2.5" : "mt-4"), sm ? "text-[14px]" : "text-[17px]")}>{title}</p>
      {hint && (
        <p className={cn("max-w-sm leading-relaxed text-slate-500", sm ? "mt-0.5 text-[12.5px]" : "mt-1 text-[13.5px]")}>{hint}</p>
      )}
      {action && <div className={sm ? "mt-3" : "mt-4"}>{action}</div>}
    </div>
  );
}
